import React from "react"
import PropTypes from "prop-types"
import { format } from "date-fns-tz"
import {
  parseISO,
  differenceInMinutes,
  add,
  isEqual as isDateEqual,
} from "date-fns"
import slugify from "lib/slugify"
import AgendaTalk, { pixelsPerMinute } from "components/AgendaTalk"
import TalkCard from "components/TalkCard"
import DateNavButtons from "components/DateNavButtons"
import theme from "theme"

const AgendaGrid = ({ talks }) => {
  const talksByDate = Object.entries(
    talks.reduce((dates, talk) => {
      const date = format(parseISO(talk.time), "EEEE, LLLL do")

      if (!dates[date]) {
        dates[date] = [talk]
      } else {
        dates[date].push(talk)
      }

      return dates
    }, {})
  )

  return (
    <div>
      {talksByDate.length > 1 && (
        <DateNavButtons dates={talksByDate.map(([, talks]) => talks[0].time)} />
      )}

      {talksByDate.map(([date, talksForDate]) => (
        <React.Fragment key={date}>
          <div
            css={{
              padding: "32px 24px",
              borderBottom: `2px solid ${theme.black}`,
              [theme.tablet]: {
                borderBottom: 0,
                ".keynote + &": {
                  borderTop: `2px solid ${theme.black}`,
                },
              },
            }}
          >
            <h2 css={theme.h1} id={slugify(date)}>
              {date}
            </h2>
          </div>

          {splitOnKeynote(talksForDate).map((talks, index) => (
            <SingleDayAgenda key={index} talks={talks} index={index} />
          ))}
        </React.Fragment>
      ))}
    </div>
  )
}

export default AgendaGrid

AgendaGrid.propTypes = {
  talks: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.shape({
        current: PropTypes.string.isRequired,
      }).isRequired,
      time: PropTypes.string.isRequired,
      duration: PropTypes.number.isRequired,
      stage: PropTypes.oneOf(["1", "2", "3", "4"]),
    }).isRequired
  ).isRequired,
}

const SingleDayAgenda = ({ talks: talksForDate, index }) => {
  const times = produceTimeMarkers(talksForDate, { minimumGapInMinutes: 30 })
  const allTimes = produceTimeMarkers(talksForDate, { minimumGapInMinutes: 0 })
  const startTime = times[0]
  const keynoteMode = talksForDate[0].keynote

  const talksByStage = Object.values(
    talksForDate.reduce((byStage, talk) => {
      if (byStage[talk.stage]) {
        byStage[talk.stage].push(talk)
      } else {
        byStage[talk.stage] = [talk]
      }

      return byStage
    }, {})
  )

  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "max(20%, 175px) 1fr",
        "--divider": `2px solid ${theme.black}`,
        ":last-child": {
          "--divider": 0,
        },
        [theme.tablet]: {
          gridTemplateColumns: "1fr",
          "--divider": keynoteMode && 0,
        },
      }}
      className={keynoteMode ? "keynote" : null}
    >
      <div
        css={{
          background: theme.gold,
          borderRight: `2px solid ${theme.black}`,
          borderBottom: "var(--divider)",
          padding: 24,
          [theme.tablet]: {
            display: "none",
          },
        }}
      >
        {times.map((time, index) => {
          const offset =
            index > 0 ? differenceInMinutes(time, times[index - 1]) : 0
          return (
            <div
              key={time}
              css={{
                marginTop: `calc(${pixelsPerMinute} * ${offset})`,
                height: 0,
              }}
            >
              {format(time, "h:mmaaa zzz")}
            </div>
          )
        })}
      </div>

      <div
        css={{
          display: "grid",
          gridTemplateColumns: keynoteMode ? "1fr" : "repeat(4, 1fr)",
          overflow: "hidden",
          borderBottom: "var(--divider)",
          ":last-of-type > div": {
            borderBottom: 0,
          },
          [theme.tablet]: {
            gridTemplateColumns: "1fr",
            borderTop: index === 0 && keynoteMode && `2px solid ${theme.black}`,
          },
        }}
      >
        {talksForDate[0].keynote
          ? talksForDate.map(({ _id, ...talk }) => (
              <TalkCard key={_id} {...talk} />
            ))
          : talksByStage.map(talks => (
              <div
                key={talks[0].stage}
                css={{
                  marginTop: -2,
                  borderBottom: `2px solid ${theme.black}`,
                  ":first-of-type": {
                    marginLeft: -2,
                  },
                  [theme.tablet]: {
                    display: "contents",
                  },
                }}
              >
                {talks.map(({ _id, ...talk }, index) => (
                  <AgendaTalk
                    key={_id}
                    offsetMinutes={differenceInMinutes(
                      parseISO(talk.time),
                      talks[index - 1]
                        ? add(parseISO(talks[index - 1].time), {
                            minutes: talks[index - 1].duration,
                          })
                        : startTime
                    )}
                    css={{
                      [theme.tablet]: {
                        order: allTimes.findIndex(time =>
                          isDateEqual(parseISO(talk.time), time)
                        ),
                      },
                    }}
                    {...talk}
                  />
                ))}
              </div>
            ))}
      </div>
    </div>
  )
}

SingleDayAgenda.propTypes = {
  talks: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.string.isRequired,
      duration: PropTypes.number.isRequired,
      stage: PropTypes.oneOf(["1", "2", "3", "4"]),
    }).isRequired
  ).isRequired,
}

const produceTimeMarkers = (talks, { minimumGapInMinutes = 30 } = {}) => {
  // Convert to array of timestamps; sort chronologically if not already
  let times = talks.map(talk => parseISO(talk.time)).sort((a, b) => a - b)

  // Eliminate duplicates
  times = Array.from(new Set(times))

  // Drop markers that are too close together
  times = times.reduce((output, time) => {
    const lastMarker = output[output.length - 1]
    if (
      !lastMarker ||
      differenceInMinutes(time, lastMarker) >= minimumGapInMinutes
    ) {
      output.push(time)
    }
    return output
  }, [])

  return times
}

// This serves to break up an array of talks that can be either non-keynote
// or keynote types into an array of  smaller arrays that are either one or
// the other, all while remaining ordered by the talk time. This output
// makes it easier to render full-width keynotes without disrupting how we
// render the non-keynote types.
const splitOnKeynote = talks =>
  talks.reduce((output, talk) => {
    const lastChunk = output[output.length - 1]
    // If the output is not empty and the last item
    // added to the last item in the output is the
    // same keynote or non-keynote status as this one,
    // combine them.
    if (
      lastChunk?.length > 0 &&
      Boolean(lastChunk[lastChunk.length - 1]?.keynote) ===
        Boolean(talk.keynote)
    ) {
      output[output.length - 1].push(talk)
    } else {
      // Create a new array to hold the distinct content
      // and add this talk to it.
      output.push([talk])
    }

    return output
  }, [])
